import EmbeddedSignup from "@/components/whatsapp/EmbeddedSignup";
import Groups from "@/components/whatsapp/Groups";
import PlatformTemplates from "@/components/whatsapp/PlatformTemplates";
import Templates from "@/components/whatsapp/Templates";
import WhatsAppCostDashboard from "@/pages/WhatsAppCostDashboard";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { whatsappService, type WhatsAppStatus } from "@/services/whatsapp.service/whatsapp.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, CheckCircle2, Copy, Loader2, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const WA_TABS = ["signup", "manual", "templates", "platform-templates", "groups", "costs"] as const;
type WaTab = (typeof WA_TABS)[number];

// ─── Manual form schema ───────────────────────────────────────────────────────

const manualSchema = z.object({
  wabaId: z.string().min(1, "WABA ID obrigatório"),
  phoneNumberId: z.string().min(1, "Phone Number ID obrigatório"),
  accessToken: z.string().min(1, "Access Token obrigatório"),
  businessId: z.string().optional(),
  name: z.string().optional(),
  appSecret: z.string().optional(),
});

type ManualForm = z.infer<typeof manualSchema>;

// ─── Copy button ──────────────────────────────────────────────────────────────

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md border border-border p-2 transition-colors hover:bg-accent"
      aria-label="Copiar"
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4 text-muted-foreground" />
      )}
    </button>
  );
}

// ─── Webhook info panel ───────────────────────────────────────────────────────

function WebhookInfoPanel() {
  const [info, setInfo] = useState<{ callbackUrl: string; verifyToken: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [data] = await whatsappService.webhookInfo();
      setLoading(false);
      if (data) setInfo(data);
    }
    void load();
  }, []);

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!info) return null;

  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          URL de callback (Webhook URL)
        </label>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded-md border border-border bg-muted px-3 py-2 text-sm font-mono">
            {info.callbackUrl}
          </code>
          <CopyButton value={info.callbackUrl} />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Verify Token
        </label>
        <div className="flex items-center gap-2">
          <code className="flex-1 break-all rounded-md border border-border bg-muted px-3 py-2 text-sm font-mono">
            {info.verifyToken}
          </code>
          <CopyButton value={info.verifyToken} />
        </div>
      </div>
    </div>
  );
}

// ─── Manual setup form ────────────────────────────────────────────────────────

interface ManualSetupFormProps {
  initialValues?: Partial<Pick<ManualForm, "wabaId" | "phoneNumberId" | "businessId" | "name">>;
  onSaved: () => void;
}

function ManualSetupForm({ initialValues, onSaved }: ManualSetupFormProps) {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<ManualForm>({
    resolver: zodResolver(manualSchema),
    defaultValues: {
      wabaId: initialValues?.wabaId || "",
      phoneNumberId: initialValues?.phoneNumberId || "",
      accessToken: "",
      businessId: initialValues?.businessId || "",
      name: initialValues?.name || "",
      appSecret: "",
    },
  });

  async function onSubmit(values: ManualForm) {
    setSubmitting(true);
    const [res, err] = await whatsappService.manual({
      wabaId: values.wabaId,
      phoneNumberId: values.phoneNumberId,
      accessToken: values.accessToken,
      businessId: values.businessId || undefined,
      name: values.name || undefined,
      appSecret: values.appSecret || undefined,
    });
    setSubmitting(false);

    if (err || !res?.success) {
      toast.error("Erro ao configurar canal manual");
      return;
    }

    toast.success("Canal WhatsApp configurado com sucesso!");
    onSaved();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="wabaId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>WABA ID</FormLabel>
                <FormControl>
                  <Input placeholder="123456789012345" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phoneNumberId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number ID</FormLabel>
                <FormControl>
                  <Input placeholder="123456789012345" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="businessId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business ID (opcional)</FormLabel>
                <FormControl>
                  <Input placeholder="123456789012345" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome do canal (opcional)</FormLabel>
                <FormControl>
                  <Input placeholder="Minha empresa" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="accessToken"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Access Token</FormLabel>
              <FormControl>
                <Input type="password" placeholder="EAA..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="appSecret"
          render={({ field }) => (
            <FormItem>
              <FormLabel>App Secret (opcional)</FormLabel>
              <FormControl>
                <Input type="password" placeholder="App Secret do Facebook" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={submitting} className="gap-2">
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Salvar configuração
        </Button>
      </form>
    </Form>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function WhatsAppSettings() {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchStatus = useCallback(async () => {
    const [data] = await whatsappService.status();
    setStatus(data);
    setLoadingStatus(false);
  }, []);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  const configured = !!status?.configured;
  const rawTab = searchParams.get("waTab");
  const reconfiguring = searchParams.get("waView") === "setup";
  const showSetup = !configured || reconfiguring;
  const activeTab: WaTab = WA_TABS.includes(rawTab as WaTab)
    ? (rawTab as WaTab)
    : configured
      ? "manual"
      : "signup";

  function openSetup(tab: WaTab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("waView", "setup");
        next.set("waTab", tab);
        return next;
      },
      { replace: true }
    );
  }

  function closeSetup() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("waView");
        next.delete("waTab");
        return next;
      },
      { replace: true }
    );
  }

  function handleSaved() {
    closeSetup();
    void fetchStatus();
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">WhatsApp Business</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Configure sua integração com a WhatsApp Cloud API
        </p>
      </div>

      {loadingStatus ? (
        <Skeleton className="h-40 w-full" />
      ) : !showSetup ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 shrink-0 text-green-500" />
              <div>
                <h2 className="font-semibold">{status?.name || "WhatsApp"} conectado</h2>
                <p className="text-sm text-muted-foreground">
                  {status?.displayPhoneNumber || `Phone Number ID: ${status?.phoneNumberId}`}
                </p>
              </div>
            </div>
            <Button variant="outline" className="shrink-0 gap-2" onClick={() => openSetup("manual")}>
              <RotateCcw className="h-4 w-4" />
              Reiniciar
            </Button>
          </div>

          <div className="rounded-lg border border-border p-6">
            <h2 className="mb-1 font-semibold">Dados do Webhook</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Configure estes valores no painel do Facebook Developer para receber eventos do
              WhatsApp.
            </p>
            <WebhookInfoPanel />
          </div>

          <div className="rounded-lg border border-border p-6">
            <Templates />
          </div>

          <div className="rounded-lg border border-border p-6">
            <PlatformTemplates />
          </div>

          <div className="rounded-lg border border-border p-6">
            <Groups />
          </div>

          <div className="rounded-lg border border-border p-6">
            <WhatsAppCostDashboard />
          </div>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={(v) => openSetup(v as WaTab)} className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <TabsList>
              <TabsTrigger value="signup">Ativação automática</TabsTrigger>
              <TabsTrigger value="manual">Configuração manual</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="platform-templates">Templates de plataforma</TabsTrigger>
              <TabsTrigger value="groups">Grupos</TabsTrigger>
              <TabsTrigger value="costs">Custos</TabsTrigger>
            </TabsList>
            {configured && (
              <Button variant="ghost" size="sm" onClick={closeSetup}>
                Cancelar
              </Button>
            )}
          </div>

          {/* Tab: Embedded Signup */}
          <TabsContent value="signup" className="space-y-4">
            <div className="rounded-lg border border-border p-6">
              <EmbeddedSignup onSuccess={handleSaved} />
            </div>
          </TabsContent>

          {/* Tab: Manual */}
          <TabsContent value="manual" className="space-y-6">
            <div className="rounded-lg border border-border p-6">
              <h2 className="mb-1 font-semibold">Dados do Webhook</h2>
              <p className="mb-4 text-sm text-muted-foreground">
                Configure estes valores no painel do Facebook Developer para receber eventos do
                WhatsApp.
              </p>
              <WebhookInfoPanel />
            </div>

            <div className="rounded-lg border border-border p-6">
              <h2 className="mb-1 font-semibold">Configuração manual</h2>
              <p className="mb-4 text-sm text-muted-foreground">
                Use esta opção se já tem um Access Token permanente de System User.
              </p>
              <ManualSetupForm initialValues={configured ? status ?? undefined : undefined} onSaved={handleSaved} />
            </div>
          </TabsContent>

          {/* Tab: Templates */}
          <TabsContent value="templates">
            <div className="rounded-lg border border-border p-6">
              <Templates />
            </div>
          </TabsContent>

          {/* Tab: Templates de plataforma */}
          <TabsContent value="platform-templates">
            <div className="rounded-lg border border-border p-6">
              <PlatformTemplates />
            </div>
          </TabsContent>

          {/* Tab: Groups */}
          <TabsContent value="groups">
            <div className="rounded-lg border border-border p-6">
              <Groups />
            </div>
          </TabsContent>

          {/* Tab: Custos */}
          <TabsContent value="costs">
            <div className="rounded-lg border border-border p-6">
              <WhatsAppCostDashboard />
            </div>
          </TabsContent>
        </Tabs>
      )}
    </>
  );
}
